import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Col, Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { useStoreActions, useStoreState } from "easy-peasy";
import { useSearchParams } from "react-router-dom";
import PropTypes from "prop-types";
import DynamicTable from "../../widgets/dynamicTable/DynamicTable.jsx";
import HelperMetiers360 from "../../../services/HelpersMetiers360.js";
import { useToast } from "../../../hooks/useToast.jsx";
import ProductPipedriveButtonComponent from "./ProductPipedriveButtonComponent.jsx";

const ProductsTableComponent = ({ products }) => {
    const { apiData: { BACKOFFICE_DEFAULT_VALUES } } = useStoreState(state => state.actionSlugs);
    const { typeOfHeadset } = BACKOFFICE_DEFAULT_VALUES;
    const { updateProductAvailability, updateProductHeadset } = useStoreActions(actions => actions.orders);
    const toast = useToast();

    const [searchParam, setSearchParam] = useSearchParams();

    const [isFiltersOpen, setIsFiltersOpen] = useState(false);
    const [search, setSearch] = useState(searchParam.get("search") || "");
    const [typeFilter, setTypeFilter] = useState(searchParam.get("type") || "");
    const [axonautFilter, setAxonautFilter] = useState(searchParam.get("axonaut") || "yes");
    const [availabilityFilter, setAvailabilityFilter] = useState(searchParam.get("availability") || "yes");
    const [headsetFilter, setHeadsetFilter] = useState(searchParam.get("headset") || "");
    const [pipedriveFilter, setPipedriveFilter] = useState(searchParam.get("pipedrive") || "all");
    const [anomaliesFilter, setAnomaliesFilter] = useState(searchParam.get("anomalies") || "all");

    const [editingHeadsetId, setEditingHeadsetId] = useState(null);

    const setParamsToUrl = (newParams = {}) => {
        const currentParams = Object.fromEntries(searchParam.entries());
        setSearchParam({ ...currentParams, ...newParams });
    };

    useEffect(() => {
        setParamsToUrl({
            search,
            type: typeFilter,
            axonaut: axonautFilter,
            availability: availabilityFilter,
            headset: headsetFilter,
            pipedrive: pipedriveFilter,
            anomalies: anomaliesFilter,
        });
    }, [search, typeFilter, axonautFilter, availabilityFilter, headsetFilter, pipedriveFilter, anomaliesFilter]);

    const areFiltersApplied = search !== "" || typeFilter !== "" || axonautFilter !== "all"
        || availabilityFilter !== "all" || headsetFilter !== "" || pipedriveFilter !== "all" || anomaliesFilter !== "all";

    const resetFilters = () => {
        setSearch("");
        setTypeFilter("");
        setAxonautFilter("all");
        setAvailabilityFilter("all");
        setHeadsetFilter("");
        setPipedriveFilter("all");
        setAnomaliesFilter("all");
    };

    const productTypes = useMemo(() => {
        const types = [...new Set(products.map(p => p.type).filter(Boolean))].sort();
        return types;
    }, [products]);

    const filteredProducts = useMemo(() => {
        return products.filter(p => {
            if (search && !HelperMetiers360.isSearchInText(search, p.name)) return false;
            if (typeFilter && p.type !== typeFilter) return false;
            if (axonautFilter === "yes" && !p.isAxonautAvailable) return false;
            if (axonautFilter === "no" && p.isAxonautAvailable) return false;
            if (availabilityFilter === "yes" && !(p.type !== "Service" && p.isAvailable)) return false;
            if (availabilityFilter === "no" && (p.type !== "Service" && p.isAvailable)) return false;
            if (headsetFilter && p.reference !== headsetFilter) return false;
            if (pipedriveFilter === "yes" && p.idPipedrive == null) return false;
            if (pipedriveFilter === "no" && p.idPipedrive != null) return false;
            if (anomaliesFilter === "yes" && !(p.anomalies?.length > 0)) return false;
            return true;
        });
    }, [products, search, typeFilter, axonautFilter, availabilityFilter, headsetFilter, pipedriveFilter, anomaliesFilter]);

    const headsetLabelByReference = useMemo(() => {
        return typeOfHeadset?.reduce((acc, h) => { acc[h.reference] = h.label; return acc; }, {}) ?? {};
    }, [typeOfHeadset]);

    const unlinkedPipedriveCount = useMemo(() => {
        return products.filter(p => p.isAxonautAvailable && p.idPipedrive == null).length;
    }, [products]);

    const handleUpdateAvailability = (uniqueId, isAvailable) => {
        updateProductAvailability({ uniqueId, isAvailable })
            .catch(() => toast.open({ message: "Erreur lors de la mise à jour de la disponibilité", variant: "danger" }));
    };

    const handleUpdateHeadset = (uniqueId, reference) => {
        updateProductHeadset({ uniqueId, reference })
            .then(() => setEditingHeadsetId(null))
            .catch(() => toast.open({ message: "Erreur lors de la mise à jour du modèle de casque", variant: "danger" }));
    };

    const sortType = filteredProducts?.length > 0 && [
        {
            value: "name",
            label: "Produit",
            test: HelperMetiers360.isArrayContainsValue(filteredProducts, "name"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.name, b.name),
            display: (product) => product.description
                ? <OverlayTrigger placement="top" overlay={<Tooltip>{product.description}</Tooltip>}>
                    <span>{product.name ?? "-"}</span>
                  </OverlayTrigger>
                : product.name ?? "-",
        },
        {
            value: "type",
            label: "Type",
            test: HelperMetiers360.isArrayContainsValue(filteredProducts, "type"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.type, b.type),
            display: (product) => product.type ?? "-",
        },
        {
            value: "isAxonautAvailable",
            label: "Statut Axonaut",
            test: true,
            method: (a, b) => (a.isAxonautAvailable === b.isAxonautAvailable ? 0 : a.isAxonautAvailable ? -1 : 1),
            display: (product) => product.isAxonautAvailable
                ? <span className="badge bg-success">Actif</span>
                : <span className="badge bg-danger">Inactif</span>,
        },
        {
            value: "idPipedrive",
            label: "PipeDrive",
            test: true,
            method: (a, b) => (a.idPipedrive ?? 0) - (b.idPipedrive ?? 0),
            display: (product) => <ProductPipedriveButtonComponent product={product} />,
        },
        {
            value: "anomalies",
            label: "Anomalies",
            test: true,
            method: (a, b) => (b.anomalies?.length ?? 0) - (a.anomalies?.length ?? 0),
            display: (product) => {
                if (!product.anomalies?.length) return <span className="text-muted">—</span>;
                return <OverlayTrigger placement="top"
                    overlay={<Tooltip>{product.anomalies.map(a => a.code).join(", ")}</Tooltip>}>
                    <Badge bg="warning" text="dark">{product.anomalies.length}</Badge>
                </OverlayTrigger>;
            },
        },
        {
            value: "isAvailable",
            label: "Dispo bons de prép.",
            test: true,
            method: (a, b) => (a.isAvailable === b.isAvailable ? 0 : a.isAvailable ? -1 : 1),
            display: (product) => product.type === "Service"
                ? <span className="badge bg-secondary">Service</span>
                : !product.isAxonautAvailable
                ? <span className="text-muted">—</span>
                : <Form.Check
                    type="switch"
                    id={`availability-${product.uniqueId}`}
                    checked={!!product.isAvailable}
                    onChange={(e) => { e.stopPropagation(); handleUpdateAvailability(product.uniqueId, e.target.checked); }}
                    onClick={(e) => e.stopPropagation()}
                    className="m-0 d-flex align-items-center justify-content-center"
                />,
        },
        {
            value: "reference",
            label: "Modèle de casque",
            test: true,
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(
                headsetLabelByReference[a.reference],
                headsetLabelByReference[b.reference]
            ),
            display: (product) => {
                if (product.type === "Service" || !product.isAvailable) {
                    return <span className="text-muted">—</span>;
                }
                if (editingHeadsetId === product.uniqueId) {
                    return <Form.Select
                        size="sm"
                        value={product.reference ?? ""}
                        autoFocus
                        onChange={(e) => { e.stopPropagation(); handleUpdateHeadset(product.uniqueId, e.target.value); }}
                        onBlur={() => setEditingHeadsetId(null)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ minWidth: "160px" }}
                    >
                        <option value="">— Aucun —</option>
                        {typeOfHeadset?.map(h => <option key={h.reference} value={h.reference}>{h.label}</option>)}
                    </Form.Select>;
                }
                return <span className="d-flex align-items-center justify-content-between w-100">
                    <span>{headsetLabelByReference[product.reference] ?? <span className="text-muted">— Aucun —</span>}</span>
                    <Button
                        size="sm"
                        variant="outline-secondary"
                        className="p-1 lh-1 ms-2"
                        onClick={(e) => { e.stopPropagation(); setEditingHeadsetId(product.uniqueId); }}
                    >
                        <i className="fas fa-pencil-alt" />
                    </Button>
                </span>;
            },
        },
    ];

    return <>
        {unlinkedPipedriveCount > 0 && <Row className="mb-2">
            <Col>
                <span className="text-muted">
                    <i className="fas fa-exclamation-circle me-1" />
                    {unlinkedPipedriveCount} produit{unlinkedPipedriveCount > 1 ? "s" : ""} Axonaut actif{unlinkedPipedriveCount > 1 ? "s" : ""} sans lien PipeDrive
                </span>
            </Col>
        </Row>}
        <Row className="d-flex align-items-center pe-0 mb-1">
            <Col className="d-flex align-items-center">
                <Button id="btnFilters" className="cursor-pointer shadow"
                    onClick={() => setIsFiltersOpen(!isFiltersOpen)}
                    onKeyDown={() => setIsFiltersOpen(!isFiltersOpen)}>
                    <p className="me-3">Filtres</p>
                    {isFiltersOpen ? <i className="fa fa-caret-up" /> : <i className="fa fa-caret-down" />}
                </Button>
                {areFiltersApplied &&
                    <OverlayTrigger placement="top" overlay={<Tooltip>Effacer les filtres</Tooltip>}>
                        <Button id="btnInitFilters" className="shadow" variant="danger" size="sm" onClick={resetFilters}>
                            <i className="fas fa-redo" />
                        </Button>
                    </OverlayTrigger>
                }
            </Col>
            <Col className="pe-0">
                <Form.Control
                    type="search"
                    placeholder="Rechercher un produit..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </Col>
        </Row>
        <div id="filtersZone" className={!isFiltersOpen ? "hide-filters" : undefined}>
            <Row className="d-flex align-items-center mb-3">
                <Col className="m360-col3-start" md={6} lg={4}>
                    <div className="d-flex align-items-center">
                        <label className="label-select">Type&nbsp;: </label>
                        <Form.Select
                            size="sm"
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                            aria-label="Filtrer par type"
                        >
                            <option value="">Tous</option>
                            {productTypes.map(t => <option key={t} value={t}>{t}</option>)}
                        </Form.Select>
                    </div>
                </Col>
                <Col className="m360-col3-center" md={6} lg={4}>
                    <div className="d-flex align-items-center">
                        <label className="label-select">Axonaut&nbsp;: </label>
                        <div className="choices-selector-inline">
                            <div className={axonautFilter === "yes" ? "selected" : null}
                                onClick={() => setAxonautFilter("yes")} onKeyDown={() => setAxonautFilter("yes")}>
                                Actif</div>
                            <div className={axonautFilter === "all" ? "selected" : null}
                                onClick={() => setAxonautFilter("all")} onKeyDown={() => setAxonautFilter("all")}>
                                Tous</div>
                            <div className={axonautFilter === "no" ? "selected" : null}
                                onClick={() => setAxonautFilter("no")} onKeyDown={() => setAxonautFilter("no")}>
                                Inactif</div>
                        </div>
                    </div>
                </Col>
                <Col className="m360-col3-end" md={6} lg={4}>
                    <div className="d-flex align-items-center">
                        <label className="label-select">Bons de prép.&nbsp;: </label>
                        <div className="choices-selector-inline">
                            <div className={availabilityFilter === "yes" ? "selected" : null}
                                onClick={() => setAvailabilityFilter("yes")} onKeyDown={() => setAvailabilityFilter("yes")}>
                                Dispo</div>
                            <div className={availabilityFilter === "all" ? "selected" : null}
                                onClick={() => setAvailabilityFilter("all")} onKeyDown={() => setAvailabilityFilter("all")}>
                                Tous</div>
                            <div className={availabilityFilter === "no" ? "selected" : null}
                                onClick={() => setAvailabilityFilter("no")} onKeyDown={() => setAvailabilityFilter("no")}>
                                Indispo</div>
                        </div>
                    </div>
                </Col>
                <Col className="m360-col3-start mt-2" md={6} lg={4}>
                    <div className="d-flex align-items-center">
                        <label className="label-select">Casque&nbsp;: </label>
                        <Form.Select
                            size="sm"
                            value={headsetFilter}
                            onChange={(e) => setHeadsetFilter(e.target.value)}
                            aria-label="Filtrer par modèle de casque"
                        >
                            <option value="">Tous</option>
                            {typeOfHeadset?.map(h => <option key={h.reference} value={h.reference}>{h.label}</option>)}
                        </Form.Select>
                    </div>
                </Col>
                <Col className="m360-col3-center mt-2" md={6} lg={4}>
                    <div className="d-flex align-items-center">
                        <label className="label-select">PipeDrive&nbsp;: </label>
                        <div className="choices-selector-inline">
                            <div className={pipedriveFilter === "yes" ? "selected" : null}
                                onClick={() => setPipedriveFilter("yes")} onKeyDown={() => setPipedriveFilter("yes")}>
                                Liés</div>
                            <div className={pipedriveFilter === "all" ? "selected" : null}
                                onClick={() => setPipedriveFilter("all")} onKeyDown={() => setPipedriveFilter("all")}>
                                Tous</div>
                            <div className={pipedriveFilter === "no" ? "selected" : null}
                                onClick={() => setPipedriveFilter("no")} onKeyDown={() => setPipedriveFilter("no")}>
                                Non liés</div>
                        </div>
                    </div>
                </Col>
                <Col className="m360-col3-end mt-2" md={6} lg={4}>
                    <div className="d-flex align-items-center">
                        <label className="label-select">Anomalies&nbsp;: </label>
                        <div className="choices-selector-inline">
                            <div className={anomaliesFilter === "all" ? "selected" : null}
                                onClick={() => setAnomaliesFilter("all")} onKeyDown={() => setAnomaliesFilter("all")}>
                                Tous</div>
                            <div className={anomaliesFilter === "yes" ? "selected" : null}
                                onClick={() => setAnomaliesFilter("yes")} onKeyDown={() => setAnomaliesFilter("yes")}>
                                Avec anomalies</div>
                        </div>
                    </div>
                </Col>
            </Row>
        </div>
        {sortType && <DynamicTable
            contentTable={filteredProducts}
            contentSort={sortType}
            index="uniqueId"
            valueInitSort="name"
            withParams={false}
        />}
    </>;
};

ProductsTableComponent.propTypes = {
    products: PropTypes.array.isRequired,
};

export default ProductsTableComponent;
